/**
 * Alert Channels Tab — where a project sends TEAM alerts (Telegram,
 * webhook, Bitrix24 chat, in-app) and at what severity.
 *
 * Self-saving (like ChannelSettingsTab), not part of the shared
 * useProjectSettings form — it has its own fetch/save lifecycle via
 * useAlertChannels/useSaveAlertChannels/useTestAlertChannel.
 *
 * Backend is complete and unchanged
 * (docs/plans/2026-07-27-team-alert-channels-ui.md). Two behaviors this
 * component exists to get right:
 * - A masked secret (`•••••1234`) is shown only as a placeholder, never as
 *   a field value. The write endpoint treats ANY non-empty string as a new
 *   secret — sending the mask back would overwrite the real bot token /
 *   webhook secret with the mask itself and silently break delivery.
 * - The write is a full replace of alertNotificationChannels, not a merge:
 *   every save must resend every channel's current state, including ones
 *   the user didn't touch this session — see buildPayload().
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SettingsSaveBar } from '@/components/settings/SettingsSaveBar';
import { useReportUnsavedSection } from '@/components/settings/UnsavedChangesGuard';
import { Switch } from '@/components/ui/switch';
import { ToggleGrid } from '@/components/ui/toggle-grid';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useRole } from '@/contexts/RoleContext';
import { useToast } from '@/contexts/ToastContext';
import {
  useAlertChannels,
  useSaveAlertChannels,
  useTestAlertChannel,
} from '@/features/alerts/hooks/useAlertChannels';
import type { AlertNotificationChannels } from '@aica/api-types/schemas';

// project's own security rules (.claude/rules/core/security.md), not a

// Email is absent on purpose: it was offered here, saved, and even passed the
// test send, but `deliverToTeamChannels` only ever implemented Telegram,
// Bitrix24 chat and webhook — a live alert never reached it (Codex P1). The
// channel is removed rather than implemented; personal email notifications are
// a separate, working path (user notification channels).
type TestableChannel = 'telegram' | 'webhook' | 'bitrix24_chat';

export interface FormState {
  minSeverity: 'low' | 'medium' | 'high' | 'critical';
  telegram: { enabled: boolean; chatId: string; botToken: string };
  webhook: { enabled: boolean; url: string; secret: string };
  bitrix24Chat: { enabled: boolean; chatId: string };
  inApp: { enabled: boolean };
}

function buildFormState(data: AlertNotificationChannels | undefined): FormState {
  return {
    minSeverity: data?.teamFilter?.minSeverity ?? 'high',
    telegram: {
      enabled: data?.telegram?.enabled ?? false,
      chatId: data?.telegram?.chatId ?? '',
      // NEVER prefilled from the masked value — see file header.
      botToken: '',
    },
    webhook: {
      enabled: data?.webhook?.enabled ?? false,
      url: data?.webhook?.url ?? '',
      secret: '',
    },
    bitrix24Chat: {
      enabled: data?.bitrix24_chat?.enabled ?? false,
      chatId: data?.bitrix24_chat?.chatId ?? '',
    },
    inApp: { enabled: data?.inApp?.enabled ?? true },
  };
}

/**
 * Full replace — every save resends every channel, including ones the user
 * didn't touch (see file header). email/telegram/inApp/teamFilter tolerate
 * empty/default sub-fields under the schema (no min-length on telegram's
 * chatId, recipients can be an empty array) so they're always included.
 * webhook.url and bitrix24_chat.chatId are NOT optional once their parent
 * key is present (a valid https URL / a non-empty numeric id respectively)
 * — sending them empty would 400 — so those two are included only when
 * `form` currently holds a value.
 *
 * `form` alone is the right condition, deliberately NOT "was there ever a
 * saved value" (review finding I2, an earlier version of this function did
 * check that too, via an `original` parameter — removed here). buildFormState
 * pre-fills `form.webhook.url`/`form.bitrix24Chat.chatId` straight from the
 * loaded data, so for a session that never touches either card the field is
 * already the saved value and this still resends it unchanged. The only way
 * `form.webhook.url` can be empty here is the user deliberately clearing the
 * input — and clearing it MUST be able to omit the key, because that's the
 * only way this screen can ever disconnect a webhook or Bitrix24 chat: full
 * replace means "key absent" is what tells the backend to drop it. Checking
 * `original` too, as before, made that impossible: `original?.webhook` stays
 * truthy after the field is cleared, so the key kept getting resent with an
 * empty `url`, which fails `.url()` server-side and 400s the WHOLE save —
 * including an edit to something unrelated, like the severity threshold.
 * Omitting an unconfigured channel is safe: there is nothing to overwrite.
 */
/**
 * Exported for the contract test: the request this form builds must satisfy
 * `alertNotificationChannelsWriteSchema`, the very schema the PUT validates
 * with. Before that test the agreement was a matter of reading two files and
 * believing they matched (review B31).
 */
export function buildPayload(form: FormState): AlertNotificationChannels {
  const payload: AlertNotificationChannels = {
    telegram: {
      enabled: form.telegram.enabled,
      chatId: form.telegram.chatId,
      botToken: form.telegram.botToken,
    },
    inApp: { enabled: form.inApp.enabled },
    teamFilter: { minSeverity: form.minSeverity },
  };

  if (form.webhook.url) {
    payload.webhook = {
      enabled: form.webhook.enabled,
      url: form.webhook.url,
      secret: form.webhook.secret,
    };
  }

  if (form.bitrix24Chat.chatId) {
    payload.bitrix24_chat = {
      enabled: form.bitrix24Chat.enabled,
      chatId: form.bitrix24Chat.chatId,
    };
  }

  return payload;
}

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'] as const;

interface AlertChannelsTabProps {
  projectId: string;
}

export function AlertChannelsTab({ projectId }: AlertChannelsTabProps) {
  const { t } = useTranslation(['projects', 'common', 'alerts']);
  const { toast } = useToast();
  const { selectedOrgId, canEditProject } = useRole();
  // The settings page's own outer gate (index.tsx) already requires
  // update:project before ANY tab mounts, so this is currently always true
  // in practice — kept as a real, independent check (not a copy of that
  // gate's result) per the plan's access step, and as a guard against the
  // outer gate ever changing without this tab being revisited.
  const canEdit = !!selectedOrgId && canEditProject(projectId, selectedOrgId);

  const { data, isLoading, isError } = useAlertChannels(projectId);
  const saveMutation = useSaveAlertChannels(projectId);
  const testMutation = useTestAlertChannel(projectId);

  // null = not yet initialized from server. Same pattern as
  // ChannelSettingsTab: local form state is a snapshot the user edits,
  // reset to null (re-synced from fresh data) after a successful save.
  const [form, setForm] = useState<FormState | null>(null);
  const [testingChannel, setTestingChannel] = useState<TestableChannel | null>(null);

  // Mirrors `form` on every render (not just at effect time) so the sync
  // effect below can read the latest value without listing `form` as a
  // dependency — see the effect's own comment for why that matters.
  const formRef = useRef(form);
  formRef.current = form;
  // The server-derived form as of the last time `form` was set FROM the
  // server (initial load, or a re-sync below) — distinct from `savedForm`
  // just below, which always reflects the CURRENT `data` and therefore
  // already changes the instant a background refetch lands, before this
  // effect gets a chance to compare against it.
  const lastSyncedFormRef = useRef<FormState | null>(null);

  useEffect(() => {
    if (!data) return;
    const freshForm = buildFormState(data);
    const currentForm = formRef.current;
    // Review finding M2: `staleTime` is 30s but nothing disables
    // `refetchOnReconnect`, so a network reconnect on a tab that's been
    // open longer than that silently refetches `data` — e.g. after someone
    // else configures a channel through another session. Seeding `form`
    // only once (the original `form === null` check) meant that refetch
    // never reached `form`, so buildPayload() kept building a payload from
    // the STALE snapshot. Before the fix that eliminated `original` from
    // buildPayload's webhook/bitrix24_chat checks (review I2), that stale
    // snapshot at least 400'd loudly (an empty required field slipping
    // through); after I2 it does something worse — it silently OMITS the
    // key, quietly reverting a channel someone else just set up. The fix
    // isn't reverting I2 (the 400 was worse for the ordinary "user clears
    // a field" case); it's re-seeding `form` whenever there are no unsaved
    // edits to lose, so the stale-snapshot window closes instead of
    // needing the disjunct.
    if (
      currentForm === null ||
      JSON.stringify(currentForm) === JSON.stringify(lastSyncedFormRef.current)
    ) {
      setForm(freshForm);
      lastSyncedFormRef.current = freshForm;
    }
    // Deps are just `[data]` — `formRef`/`lastSyncedFormRef` are refs, so
    // the lint rule doesn't require them here, and that's exactly what's
    // wanted: this must run only when `data` changes, not on every
    // keystroke.
  }, [data]);

  const savedForm = useMemo(() => (data ? buildFormState(data) : null), [data]);

  /**
   * The write schema takes https only, while the read schema (deliberately)
   * still returns whatever is on disk — so a webhook configured before that
   * rule loads into this form and is resubmitted verbatim on the next save,
   * failing the PUT with a generic toast no matter which field was actually
   * edited (review B30). The same dead end is one keystroke away without any
   * legacy data: `type="url"` accepts a typed `http://` perfectly happily.
   *
   * Named at the field instead: the whole request is rejected by the
   * endpoint, so letting the user press Save could only produce that same
   * useless toast.
   */
  const webhookUrlError =
    form && form.webhook.url && !form.webhook.url.startsWith('https://')
      ? t(
          'projects.alertChannelsWebhookUrlMustBeHttps',
          'The address must start with https:// — update it or clear the field to disconnect the webhook.'
        )
      : null;

  const handleSave = () => {
    if (!form) return;
    saveMutation.mutate(buildPayload(form), {
      onSuccess: () => {
        toast.success(t('projects.alertChannelsSaved', 'Alert delivery settings saved'));
        setForm(null); // re-sync from the freshly invalidated query
      },
      onError: (err) => {
        toast.error(
          t('projects.alertChannelsSaveFailed', 'Failed to save alert delivery settings'),
          err instanceof Error ? err.message : undefined
        );
      },
    });
  };

  const handleTest = (channel: TestableChannel) => {
    setTestingChannel(channel);
    testMutation.mutate(
      { channel },
      {
        onSuccess: (result) => {
          // Never show success unless the response says so explicitly —
          // a 200 response can still carry { success: false, error }.
          if (result.success) {
            toast.success(
              t('projects.alertChannelsTestSuccess', 'Test notification sent')
            );
          } else {
            toast.error(
              t('projects.alertChannelsTestFailed', 'Test notification failed'),
              result.error
            );
          }
        },
        onError: (err) => {
          toast.error(
            t('projects.alertChannelsTestFailed', 'Test notification failed'),
            err instanceof Error ? err.message : undefined
          );
        },
        onSettled: () => setTestingChannel(null),
      }
    );
  };

  // Above the early returns, because a hook cannot sit below one: this tab
  // saves itself, so the screen's shared `isDirty` is blind to its edits and
  // a section switch unmounts the tab with them still in it. `savedForm` is
  // null until the first response arrives, which is exactly when there is
  // nothing to lose yet.
  useReportUnsavedSection(
    'project-settings:alert-channels',
    savedForm !== null && JSON.stringify(form) !== JSON.stringify(savedForm)
  );

  // Order matters: isError must be checked before the loading/not-yet-
  // initialized branch below. On a read failure `data` stays undefined, so
  // the effect that seeds `form` from it never runs and `form` stays `null`
  // forever — `isLoading || form === null` would then be permanently true
  // and the error branch below would never be reached (review finding I1).
  if (isError) {
    return (
      <p className="text-sm text-danger" data-testid="alert-channels-error">
        {t('projects.alertChannelsLoadFailed', 'Failed to load alert delivery settings')}
      </p>
    );
  }

  if (isLoading || form === null) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-fg-muted" />
      </div>
    );
  }

  const fieldsDisabled = !canEdit;

  // A channel's Test button is active only when it's enabled AND the
  // current save state (not the unsaved local edits) already has it
  // enabled — testing something not yet saved would test the wrong config.
  const isSavedAndEnabled = (channel: TestableChannel): boolean => {
    if (!savedForm) return false;
    switch (channel) {
      case 'telegram':
        return savedForm.telegram.enabled;
      case 'webhook':
        return savedForm.webhook.enabled;
      case 'bitrix24_chat':
        return savedForm.bitrix24Chat.enabled;
    }
  };
  const hasUnsavedChanges =
    savedForm !== null && JSON.stringify(form) !== JSON.stringify(savedForm);

  return (
    <ToggleGrid className="max-w-5xl gap-4" data-testid="alert-channels-tab">
      {!canEdit && (
        <p
          className="text-xs text-fg-muted md:col-span-2"
          data-testid="alert-channels-readonly-hint"
        >
          {t(
            'projects.alertChannelsReadOnlyHint',
            "You don't have permission to edit alert delivery settings."
          )}
        </p>
      )}

      {/* Threshold card */}
      <Card className="md:col-span-2" data-testid="alert-channels-threshold-card">
        <h3 className="text-sm font-medium text-fg mb-1">
          <Megaphone className="inline h-4 w-4 mr-2 text-fg-muted" />
          {t('projects.alertChannelsThresholdTitle', 'Team channel threshold')}
        </h3>
        <p className="text-xs text-fg-muted mb-3">
          {t(
            'projects.alertChannelsThresholdDescription',
            'The team channel only receives alerts at or above this severity. This is separate from personal alert subscriptions.'
          )}
        </p>
        <Label htmlFor="alert-channels-min-severity" size="sm">
          {t('projects.alertChannelsMinSeverityLabel', 'Minimum severity')}
        </Label>
        <Select
          id="alert-channels-min-severity"
          value={form.minSeverity}
          onValueChange={(v) =>
            setForm({ ...form, minSeverity: v as FormState['minSeverity'] })
          }
          // common.json only has low/medium/high — no `critical` key, so
          // `common:common.critical` silently fell back to the raw value
          // "critical" (review finding I4). alerts.{low,medium,high,critical}
          // is the live vocabulary the rest of the Alerts screen already
          // uses for the same severities (AlertRuleCard.tsx, -alerts.lazy.tsx)
          // and has all four in every locale.
          options={SEVERITY_OPTIONS.map((s) => ({
            value: s,
            label: t(`alerts:alerts.${s}`, s),
          }))}
          size="sm"
          disabled={fieldsDisabled}
          data-testid="alert-channels-min-severity"
        />
      </Card>

      {/* Telegram */}
      <Card data-testid="alert-channels-telegram-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-fg">
            {t('projects.alertChannelsTelegramTitle', 'Telegram')}
          </h3>
          <Switch
            checked={form.telegram.enabled}
            onCheckedChange={(v) =>
              setForm({ ...form, telegram: { ...form.telegram, enabled: v } })
            }
            disabled={fieldsDisabled}
            size="sm"
            data-testid="alert-channels-telegram-enabled"
          />
        </div>
        <p className="text-xs text-fg-muted mb-3">
          {t(
            'projects.alertChannelsTelegramDescription',
            'Send team alerts to a Telegram chat.'
          )}
        </p>
        {/* Saving with the switch off deletes the bot token from KV
            (apps/edge-api/src/routes/projects/lifecycle.ts) — warn before
            that happens rather than after, while it's still reversible
            (review finding I3). */}
        {savedForm?.telegram.enabled &&
          !form.telegram.enabled &&
          data?.telegram?.botToken && (
            <p
              className="text-xs text-warning mb-3"
              data-testid="alert-channels-telegram-secret-warning"
            >
              {t(
                'projects.alertChannelsSecretWillBeForgotten',
                'Saving with this off will delete the saved credentials.'
              )}
            </p>
          )}
        <div className="space-y-3">
          <div>
            <Label htmlFor="alert-channels-telegram-chat-id" size="sm">
              {t('projects.alertChannelsTelegramChatIdLabel', 'Chat ID')}
            </Label>
            <Input
              id="alert-channels-telegram-chat-id"
              value={form.telegram.chatId}
              onChange={(e) =>
                setForm({
                  ...form,
                  telegram: { ...form.telegram, chatId: e.target.value },
                })
              }
              placeholder={t(
                'projects.alertChannelsTelegramChatIdPlaceholder',
                'e.g. -1001234567890'
              )}
              disabled={fieldsDisabled}
              data-testid="alert-channels-telegram-chat-id"
            />
          </div>
          <div>
            <Label htmlFor="alert-channels-telegram-bot-token" size="sm">
              {t('projects.alertChannelsTelegramBotTokenLabel', 'Bot token')}
            </Label>
            <Input
              id="alert-channels-telegram-bot-token"
              type="password"
              value={form.telegram.botToken}
              onChange={(e) =>
                setForm({
                  ...form,
                  telegram: { ...form.telegram, botToken: e.target.value },
                })
              }
              placeholder={data?.telegram?.botToken || undefined}
              disabled={fieldsDisabled}
              data-testid="alert-channels-telegram-bot-token"
            />
            {/* Only true when a mask actually came back from the server —
                unconditional text here told a project with no bot token
                configured yet that a "current value" existed to keep
                (review finding I1's sibling, I3). */}
            {data?.telegram?.botToken && (
              <p className="text-xs text-fg-subtle mt-1">
                {t(
                  'projects.alertChannelsSecretHint',
                  'Leave empty to keep the current value.'
                )}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleTest('telegram')}
            disabled={
              fieldsDisabled ||
              hasUnsavedChanges ||
              !isSavedAndEnabled('telegram') ||
              testingChannel === 'telegram'
            }
            isLoading={testingChannel === 'telegram'}
            data-testid="alert-channels-telegram-test"
          >
            {t('projects.alertChannelsTest', 'Test')}
          </Button>
        </div>
      </Card>

      {/* Webhook */}
      <Card data-testid="alert-channels-webhook-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-fg">
            {t('projects.alertChannelsWebhookTitle', 'Webhook')}
          </h3>
          <Switch
            checked={form.webhook.enabled}
            onCheckedChange={(v) =>
              setForm({ ...form, webhook: { ...form.webhook, enabled: v } })
            }
            disabled={fieldsDisabled}
            size="sm"
            data-testid="alert-channels-webhook-enabled"
          />
        </div>
        <p className="text-xs text-fg-muted mb-3">
          {t(
            'projects.alertChannelsWebhookDescription',
            'POST team alerts to an HTTPS endpoint.'
          )}
        </p>
        {/* Same warning as Telegram's — saving with the switch off deletes
            the signing secret from KV (review finding I3). */}
        {savedForm?.webhook.enabled && !form.webhook.enabled && data?.webhook?.secret && (
          <p
            className="text-xs text-warning mb-3"
            data-testid="alert-channels-webhook-secret-warning"
          >
            {t(
              'projects.alertChannelsSecretWillBeForgotten',
              'Saving with this off will delete the saved credentials.'
            )}
          </p>
        )}
        <div className="space-y-3">
          <div>
            <Label htmlFor="alert-channels-webhook-url" size="sm">
              {t('projects.alertChannelsWebhookUrlLabel', 'URL')}
            </Label>
            <Input
              id="alert-channels-webhook-url"
              type="url"
              value={form.webhook.url}
              onChange={(e) =>
                setForm({ ...form, webhook: { ...form.webhook, url: e.target.value } })
              }
              placeholder={t(
                'projects.alertChannelsWebhookUrlPlaceholder',
                'https://example.com/hooks/alerts'
              )}
              disabled={fieldsDisabled}
              aria-invalid={webhookUrlError ? true : undefined}
              aria-describedby={
                webhookUrlError ? 'alert-channels-webhook-url-error' : undefined
              }
              data-testid="alert-channels-webhook-url"
            />
            {webhookUrlError && (
              <p
                id="alert-channels-webhook-url-error"
                className="text-xs text-danger mt-1"
                data-testid="alert-channels-webhook-url-error"
              >
                {webhookUrlError}
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="alert-channels-webhook-secret" size="sm">
              {t('projects.alertChannelsWebhookSecretLabel', 'Signing secret')}
            </Label>
            <Input
              id="alert-channels-webhook-secret"
              type="password"
              value={form.webhook.secret}
              onChange={(e) =>
                setForm({
                  ...form,
                  webhook: { ...form.webhook, secret: e.target.value },
                })
              }
              placeholder={data?.webhook?.secret || undefined}
              disabled={fieldsDisabled}
              data-testid="alert-channels-webhook-secret"
            />
            {/* Only true when a mask actually came back from the server —
                same fix as Telegram's bot token field (review finding I3). */}
            {data?.webhook?.secret && (
              <p className="text-xs text-fg-subtle mt-1">
                {t(
                  'projects.alertChannelsSecretHint',
                  'Leave empty to keep the current value.'
                )}
              </p>
            )}
          </div>
        </div>
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleTest('webhook')}
            disabled={
              fieldsDisabled ||
              hasUnsavedChanges ||
              !isSavedAndEnabled('webhook') ||
              testingChannel === 'webhook'
            }
            isLoading={testingChannel === 'webhook'}
            data-testid="alert-channels-webhook-test"
          >
            {t('projects.alertChannelsTest', 'Test')}
          </Button>
        </div>
      </Card>

      {/* Bitrix24 chat */}
      <Card data-testid="alert-channels-bitrix24-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-medium text-fg">
            {t('projects.alertChannelsBitrix24Title', 'Bitrix24 chat')}
          </h3>
          <Switch
            checked={form.bitrix24Chat.enabled}
            onCheckedChange={(v) =>
              setForm({
                ...form,
                bitrix24Chat: { ...form.bitrix24Chat, enabled: v },
              })
            }
            disabled={fieldsDisabled}
            size="sm"
            data-testid="alert-channels-bitrix24-enabled"
          />
        </div>
        <p className="text-xs text-fg-muted mb-3">
          {t(
            'projects.alertChannelsBitrix24Description',
            'Send team alerts to a Bitrix24 group chat.'
          )}
        </p>
        <div>
          <Label htmlFor="alert-channels-bitrix24-chat-id" size="sm">
            {t('projects.alertChannelsBitrix24ChatIdLabel', 'Chat ID')}
          </Label>
          <Input
            id="alert-channels-bitrix24-chat-id"
            inputMode="numeric"
            value={form.bitrix24Chat.chatId}
            onChange={(e) =>
              setForm({
                ...form,
                bitrix24Chat: {
                  ...form.bitrix24Chat,
                  chatId: e.target.value.replace(/\D/g, ''),
                },
              })
            }
            placeholder={t(
              'projects.alertChannelsBitrix24ChatIdPlaceholder',
              'Numeric only'
            )}
            disabled={fieldsDisabled}
            data-testid="alert-channels-bitrix24-chat-id"
          />
        </div>
        <div className="mt-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleTest('bitrix24_chat')}
            disabled={
              fieldsDisabled ||
              hasUnsavedChanges ||
              !isSavedAndEnabled('bitrix24_chat') ||
              testingChannel === 'bitrix24_chat'
            }
            isLoading={testingChannel === 'bitrix24_chat'}
            data-testid="alert-channels-bitrix24-test"
          >
            {t('projects.alertChannelsTest', 'Test')}
          </Button>
        </div>
      </Card>

      {/* In-app */}
      <Card data-testid="alert-channels-in-app-card">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-fg">
              {t('projects.alertChannelsInAppTitle', 'In-app')}
            </h3>
            <p className="text-xs text-fg-muted">
              {t(
                'projects.alertChannelsInAppDescription',
                'Show team alerts inside Aelo for everyone with access to this project.'
              )}
            </p>
          </div>
          <Switch
            checked={form.inApp.enabled}
            onCheckedChange={(v) => setForm({ ...form, inApp: { enabled: v } })}
            disabled={fieldsDisabled}
            size="sm"
            data-testid="alert-channels-in-app-enabled"
          />
        </div>
      </Card>

      {canEdit && (
        <SettingsSaveBar
          onSave={handleSave}
          isSaving={saveMutation.isPending}
          // A malformed webhook URL is the one thing this screen validates
          // client-side; saving it would only reproduce the server's 400.
          // `canSave`, not `isLoaded` — this is a validation result, not a
          // statement about whether the form has hydrated.
          canSave={webhookUrlError === null}
          label={t('projects.saveSettings', 'Save Settings')}
          buttonTestId="alert-channels-save-button"
          className="md:col-span-2"
        />
      )}
    </ToggleGrid>
  );
}
